import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHeader } from '@/contexts';
import { useListaNfse, type NfseDocumento } from '@/services/ServicosNfse';
import type { ListaNfseData } from '@/services/ServicosNfse/types';
import { getChaveAcessoDoc, parseCurrencyValue } from '../../utils';

type FiltrosHeaderAplicados = {
  empresaId: string | null;
  empresaCnpjs: string | null;
  dataInicio: string | null;
  dataFim: string | null;
};

export type DireitoCreditoFiltro = 'todos' | 'com-direito' | 'sem-direito';
export type CampoBuscaNfseFiltro = 'numeroNota' | 'prestador' | 'codigoServico' | 'chaveAcesso';
export type SortFieldNfse =
  | 'numero'
  | 'prestador'
  | 'data'
  | 'servico'
  | 'status'
  | 'valor'
  | 'ibs'
  | 'cbs';
export type SortDirectionNfse = 'asc' | 'desc';

export type FiltrosTabelaNfse = {
  campoBusca: CampoBuscaNfseFiltro;
  termoBusca: string;
  direitoCredito: DireitoCreditoFiltro;
};

type PaginacaoNfse = {
  pagina: number;
  itensPorPagina: number;
};

export type AbaNfse = 'tomados' | 'prestados';

/**
 * O total de crédito do período, quando a lista o traz. A rota nativa da lista não o devolve (o legado somava), e
 * sem ele o cartão mostrava R$ 0,00 como se fosse o total; ausente é `null`, e o cartão diz que não há o número.
 */
export function totalDoPeriodo(valor: unknown): number | null {
  if (valor === undefined || valor === null || valor === '') return null;
  return parseCurrencyValue(valor);
}

const PAGINACAO_INICIAL: PaginacaoNfse = {
  pagina: 1,
  itensPorPagina: 50,
};

const FILTROS_TABELA_INICIAIS: FiltrosTabelaNfse = {
  campoBusca: 'numeroNota',
  termoBusca: '',
  direitoCredito: 'todos',
};

function normalizeFiltroText(value: string) {
  return value.trim();
}

function getDireitoCreditoApiValue(value: DireitoCreditoFiltro): '1' | '0' | '' {
  if (value === 'com-direito') return '1';
  if (value === 'sem-direito') return '0';
  return '';
}

function getSortFieldApiValue(value: SortFieldNfse) {
  if (value === 'numero') return 'numero';
  if (value === 'prestador') return 'razao_social';
  if (value === 'data') return 'data_emissao';
  if (value === 'servico') return 'produto_codigo';
  if (value === 'status') return 'status';
  if (value === 'valor') return 'valor';
  if (value === 'ibs') return 'ibs';
  return 'cbs';
}

export function useServicosNfsePage() {
  const { filtros: filtrosHeader } = useHeader();
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [selectedDocumento, setSelectedDocumento] = useState<NfseDocumento | null>(null);
  const [selectedAba, setSelectedAba] = useState<AbaNfse | null>(null);
  const [paginacaoTomados, setPaginacaoTomados] = useState<PaginacaoNfse>(PAGINACAO_INICIAL);
  const [paginacaoPrestados, setPaginacaoPrestados] = useState<PaginacaoNfse>(PAGINACAO_INICIAL);
  const [tomadasData, setTomadasData] = useState<ListaNfseData | null>(null);
  const [prestadosData, setPrestadosData] = useState<ListaNfseData | null>(null);
  const [filtrosTabela, setFiltrosTabela] = useState<FiltrosTabelaNfse>(FILTROS_TABELA_INICIAIS);
  const [sortField, setSortField] = useState<SortFieldNfse>('data');
  const [sortDirection, setSortDirection] = useState<SortDirectionNfse>('desc');
  const [buscaAplicada, setBuscaAplicada] = useState(() => !!filtrosHeader.empresaCnpjs?.trim());

  const [filtrosAplicados, setFiltrosAplicados] = useState<FiltrosHeaderAplicados>(() => ({
    empresaId: filtrosHeader.empresaId,
    empresaCnpjs: filtrosHeader.empresaCnpjs,
    dataInicio: filtrosHeader.dataInicio,
    dataFim: filtrosHeader.dataFim,
  }));

  const filtrosTabelaApi = useMemo(() => {
    const termoBusca = normalizeFiltroText(filtrosTabela.termoBusca);

    return {
      numeroDoc: filtrosTabela.campoBusca === 'numeroNota' ? termoBusca : '',
      razaoSocial: filtrosTabela.campoBusca === 'prestador' ? termoBusca : '',
      codigoServico: filtrosTabela.campoBusca === 'codigoServico' ? termoBusca : '',
      chaveAcessoDoc: filtrosTabela.campoBusca === 'chaveAcesso' ? termoBusca : '',
      direitoCredito: getDireitoCreditoApiValue(filtrosTabela.direitoCredito),
      ordenarPor: getSortFieldApiValue(sortField),
      ordenarDirecao: sortDirection,
    };
  }, [
    filtrosTabela.campoBusca,
    filtrosTabela.direitoCredito,
    filtrosTabela.termoBusca,
    sortDirection,
    sortField,
  ]);

  const filtrosTomados = useMemo(
    () => ({
      ...filtrosAplicados,
      ...filtrosTabelaApi,
      entradaSaida: '0' as const,
      paginaNumero: paginacaoTomados.pagina,
      paginaQuantidadePor: paginacaoTomados.itensPorPagina,
    }),
    [filtrosAplicados, filtrosTabelaApi, paginacaoTomados.itensPorPagina, paginacaoTomados.pagina]
  );

  const filtrosPrestados = useMemo(
    () => ({
      ...filtrosAplicados,
      ...filtrosTabelaApi,
      entradaSaida: '1' as const,
      paginaNumero: paginacaoPrestados.pagina,
      paginaQuantidadePor: paginacaoPrestados.itensPorPagina,
    }),
    [
      filtrosAplicados,
      filtrosTabelaApi,
      paginacaoPrestados.itensPorPagina,
      paginacaoPrestados.pagina,
    ]
  );
  const selecaoEmpresaValida = !!filtrosHeader.empresaCnpjs?.trim();
  const filtrosAplicadosValidos = !!filtrosAplicados.empresaCnpjs?.trim();
  const consultasHabilitadas = buscaAplicada && selecaoEmpresaValida && filtrosAplicadosValidos;

  const {
    data: tomadasResponse,
    dataUpdatedAt: tomadasUpdatedAt,
    isLoading: isLoadingTomados,
    isFetching: isFetchingTomados,
    refetch: refetchTomados,
  } = useListaNfse(filtrosTomados, { enabled: consultasHabilitadas });

  const {
    data: prestadosResponse,
    dataUpdatedAt: prestadosUpdatedAt,
    isLoading: isLoadingPrestados,
    isFetching: isFetchingPrestados,
    refetch: refetchPrestados,
  } = useListaNfse(filtrosPrestados, { enabled: consultasHabilitadas });

  useEffect(() => {
    if (!tomadasResponse?.data) return;
    setTomadasData(tomadasResponse.data);
  }, [tomadasResponse, tomadasUpdatedAt]);

  useEffect(() => {
    if (!prestadosResponse?.data) return;
    setPrestadosData(prestadosResponse.data);
  }, [prestadosResponse, prestadosUpdatedAt]);

  const servicosTomados = tomadasData?.lista || [];
  const servicosPrestados = prestadosData?.lista || [];

  const totalServicosTomados = tomadasData?.qtd_total_registros ?? servicosTomados.length;
  const totalServicosPrestados = prestadosData?.qtd_total_registros ?? servicosPrestados.length;
  const totalPaginasTomados = Math.max(
    1,
    Math.ceil(totalServicosTomados / paginacaoTomados.itensPorPagina)
  );
  const totalPaginasPrestados = Math.max(
    1,
    Math.ceil(totalServicosPrestados / paginacaoPrestados.itensPorPagina)
  );

  const totalCreditoIBS = totalDoPeriodo(tomadasData?.total_credito_ibs);
  const totalCreditoCBS = totalDoPeriodo(tomadasData?.total_credito_cbs);
  const totalCredito =
    totalCreditoIBS === null || totalCreditoCBS === null ? null : totalCreditoIBS + totalCreditoCBS;
  const atualizando = isFetchingTomados || isFetchingPrestados;
  const isLoadingPage =
    (isLoadingTomados && !tomadasData) || (isLoadingPrestados && !prestadosData);
  const hasFiltrosTabelaAtivos =
    normalizeFiltroText(filtrosTabela.termoBusca) !== '' ||
    filtrosTabela.direitoCredito !== 'todos';
  const hasNoResults =
    !isLoadingPage &&
    !atualizando &&
    !hasFiltrosTabelaAtivos &&
    servicosTomados.length === 0 &&
    servicosPrestados.length === 0;

  useEffect(() => {
    if (paginacaoTomados.pagina <= totalPaginasTomados) return;

    setPaginacaoTomados(prev => ({ ...prev, pagina: totalPaginasTomados }));
  }, [paginacaoTomados.pagina, totalPaginasTomados]);

  useEffect(() => {
    if (paginacaoPrestados.pagina <= totalPaginasPrestados) return;

    setPaginacaoPrestados(prev => ({ ...prev, pagina: totalPaginasPrestados }));
  }, [paginacaoPrestados.pagina, totalPaginasPrestados]);

  const handleBuscar = useCallback(() => {
    if (!selecaoEmpresaValida) {
      setBuscaAplicada(false);
      return;
    }

    setPaginacaoTomados(PAGINACAO_INICIAL);
    setPaginacaoPrestados(PAGINACAO_INICIAL);
    setFiltrosAplicados({
      empresaId: filtrosHeader.empresaId,
      empresaCnpjs: filtrosHeader.empresaCnpjs,
      dataInicio: filtrosHeader.dataInicio,
      dataFim: filtrosHeader.dataFim,
    });
    setBuscaAplicada(true);
  }, [
    filtrosHeader.dataFim,
    filtrosHeader.dataInicio,
    filtrosHeader.empresaCnpjs,
    filtrosHeader.empresaId,
    selecaoEmpresaValida,
  ]);

  const handleAplicarFiltrosTabela = useCallback((filtros: FiltrosTabelaNfse) => {
    setPaginacaoTomados(PAGINACAO_INICIAL);
    setPaginacaoPrestados(PAGINACAO_INICIAL);
    setFiltrosTabela({
      campoBusca: filtros.campoBusca,
      termoBusca: normalizeFiltroText(filtros.termoBusca),
      direitoCredito: filtros.direitoCredito,
    });
  }, []);

  const handleLimparFiltrosTabela = useCallback(() => {
    setPaginacaoTomados(PAGINACAO_INICIAL);
    setPaginacaoPrestados(PAGINACAO_INICIAL);
    setFiltrosTabela(FILTROS_TABELA_INICIAIS);
  }, []);

  const handleSortChange = useCallback(
    (field: SortFieldNfse) => {
      setPaginacaoTomados(PAGINACAO_INICIAL);
      setPaginacaoPrestados(PAGINACAO_INICIAL);

      if (field === sortField) {
        setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        return;
      }

      setSortField(field);
      setSortDirection(field === 'data' ? 'desc' : 'asc');
    },
    [sortField]
  );

  const handlePaginaTomadosChange = useCallback((pagina: number) => {
    setPaginacaoTomados(prev => ({ ...prev, pagina }));
  }, []);

  const handleItensPorPaginaTomadosChange = useCallback((itensPorPagina: number) => {
    setPaginacaoTomados({ pagina: 1, itensPorPagina });
  }, []);

  const handlePaginaPrestadosChange = useCallback((pagina: number) => {
    setPaginacaoPrestados(prev => ({ ...prev, pagina }));
  }, []);

  const handleItensPorPaginaPrestadosChange = useCallback((itensPorPagina: number) => {
    setPaginacaoPrestados({ pagina: 1, itensPorPagina });
  }, []);

  const handleAtualizar = useCallback(() => {
    refetchTomados();
    refetchPrestados();
  }, [refetchPrestados, refetchTomados]);

  /* A aba diz se a nota é tomada: é dela, e não de um campo da linha, que o detalhe tira se pode manifestar
     (SPEC-011, §3, item 7 — só nas tomadas). */
  const handleSelectDocumento = useCallback((documento: NfseDocumento, aba: AbaNfse) => {
    if (!getChaveAcessoDoc(documento)) return;

    setSelectedDocumento(documento);
    setSelectedAba(aba);
    setShowDetalhesModal(true);
  }, []);

  const handleDetalhesModalChange = useCallback((open: boolean) => {
    setShowDetalhesModal(open);
    if (!open) {
      setSelectedDocumento(null);
      setSelectedAba(null);
    }
  }, []);

  /* Depois da manifestação registrada, a lista recarrega o aceite da nota. */
  const handleManifestada = useCallback(() => {
    refetchTomados();
    refetchPrestados();
  }, [refetchPrestados, refetchTomados]);

  return {
    atualizando,
    buscaAplicada,
    filtrosTabela,
    handleAtualizar,
    handleAplicarFiltrosTabela,
    handleBuscar,
    handleDetalhesModalChange,
    handleItensPorPaginaPrestadosChange,
    handleItensPorPaginaTomadosChange,
    handleLimparFiltrosTabela,
    handleManifestada,
    handlePaginaPrestadosChange,
    handlePaginaTomadosChange,
    handleSelectDocumento,
    isFetchingPrestados,
    isFetchingTomados,
    hasNoResults,
    isLoadingPrestados,
    isLoadingPage,
    isLoadingTomados,
    paginacaoPrestados,
    paginacaoTomados,
    sortDirection,
    sortField,
    selectedAba,
    selectedDocumento,
    selecaoEmpresaValida,
    servicosPrestados,
    servicosTomados,
    showDetalhesModal,
    totalCredito,
    totalCreditoCBS,
    totalCreditoIBS,
    handleSortChange,
    totalServicosPrestados,
    totalServicosTomados,
  };
}
